import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { PersonService } from "src/app/services/person.service";
import { AuthService } from "src/app/services/auth.service";
import { Person } from "src/app/models/person";
import { ErrorHandlerService } from "src/app/shared/error-handler.service";
import { ChangePasswordService } from "../../services/change-password.service";
import { Urls } from "@config/urls.config";
import { switchMap } from "rxjs";

@Component({
  selector: "app-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"],
})
export class SettingsComponent implements OnInit {
  isSaving: boolean = false;
  isCurrentPasswordVisible: boolean = false;
  isNewPasswordVisible: boolean = false;
  isConfirmPasswordVisible: boolean = false;
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  currentPerson: Person | null = null;

  settingsForm = new FormGroup({
    name: new FormControl("", [Validators.required]),
    email: new FormControl("", [Validators.required, Validators.email]),
    currentPassword: new FormControl(""),
    newPassword: new FormControl("", Validators.minLength(3)),
    confirmPassword: new FormControl("", Validators.minLength(3)),
  });

  constructor(
    private personService: PersonService,
    private authService: AuthService,
    private changePasswordService: ChangePasswordService,
    private toast: ToastrService,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
  }

  loadCurrentUser(): void {
    // Carregar foto do localStorage primeiro (mesmo método do navbar)
    const photoFromStorage = this.authService.getCurrentUserPhoto();
    if (photoFromStorage) {
      this.imagePreview = photoFromStorage;
    }

    this.personService.findByRequest().subscribe({
      next: (person: Person) => {
        this.currentPerson = person;
        this.settingsForm.patchValue({
          name: person.name || "",
          email: person.email || "",
        });

        // Carregar foto atual se existir
        // Se não temos foto do storage, tentar do backend
        if (!photoFromStorage && person.picture) {
          // Verificar se a foto já é uma URL completa
          const photoUrl =
            person.picture.startsWith("http://") || person.picture.startsWith("https://")
              ? person.picture
              : Urls.getS3() + person.picture;
          this.imagePreview = photoUrl;
        } else if (!photoFromStorage && !person.picture) {
          // Se não tem foto nem no storage nem no backend, limpar preview
          this.imagePreview = null;
        }
        // Se já tem foto do storage, manter ela (mesmo comportamento do navbar)
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Erro ao carregar dados do usuário.");
      },
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith("image/")) {
        this.toast.error("Por favor, selecione apenas arquivos de imagem.", "Erro");
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.toast.error("A imagem deve ter no máximo 5MB.", "Erro");
        return;
      }

      this.selectedFile = file;

      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target?.result || null;
      };
      reader.readAsDataURL(file);
    }
  }

  removePhoto(): void {
    this.selectedFile = null;
    // Se tinha foto anterior, manter preview da foto original
    const photoFromStorage = this.authService.getCurrentUserPhoto();
    if (photoFromStorage) {
      this.imagePreview = photoFromStorage;
    } else if (this.currentPerson?.picture) {
      // Se não tem no storage, tentar do backend
      const photoUrl = this.currentPerson.picture.startsWith("http")
        ? this.currentPerson.picture
        : Urls.getS3() + this.currentPerson.picture;
      this.imagePreview = photoUrl;
    } else {
      this.imagePreview = null;
    }
  }

  toggleCurrentPasswordVisibility(): void {
    this.isCurrentPasswordVisible = !this.isCurrentPasswordVisible;
  }

  toggleNewPasswordVisibility(): void {
    this.isNewPasswordVisible = !this.isNewPasswordVisible;
  }

  toggleConfirmPasswordVisibility(): void {
    this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
  }

  validatePasswordFields(): boolean {
    const newPassword = this.settingsForm.get("newPassword")?.value;
    const confirmPassword = this.settingsForm.get("confirmPassword")?.value;
    const currentPassword = this.settingsForm.get("currentPassword")?.value;

    // Se algum campo de senha foi preenchido, todos devem ser preenchidos
    if (newPassword || confirmPassword || currentPassword) {
      if (!currentPassword) {
        this.toast.error("A senha atual é obrigatória para alterar a senha.", "Validação");
        return false;
      }
      if (!newPassword) {
        this.toast.error("A nova senha é obrigatória.", "Validação");
        return false;
      }
      if (!confirmPassword) {
        this.toast.error("A confirmação de senha é obrigatória.", "Validação");
        return false;
      }
      if (newPassword !== confirmPassword) {
        this.toast.error("As novas senhas não são iguais.", "Validação");
        return false;
      }
    }

    return true;
  }

  onSubmit(): void {
    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      this.toast.error("Por favor, preencha todos os campos obrigatórios corretamente.", "Validação");
      return;
    }

    if (!this.validatePasswordFields()) {
      return;
    }

    this.isSaving = true;

    const userId = this.authService.getCurrentUserId();
    if (!userId || !this.currentPerson) {
      this.toast.error("Usuário não encontrado.", "Erro");
      this.isSaving = false;
      return;
    }

    // Se há alteração de foto ou dados, atualizar pessoa
    if (
      this.selectedFile ||
      this.settingsForm.get("name")?.value !== this.currentPerson.name ||
      this.settingsForm.get("email")?.value !== this.currentPerson.email
    ) {
      // Atualizar dados da pessoa
      const personToUpdate: Person = {
        ...this.currentPerson,
        name: this.settingsForm.get("name")?.value,
        email: this.settingsForm.get("email")?.value,
      };

      this.personService
        .update(userId, personToUpdate, this.selectedFile)
        .pipe(switchMap(() => this.personService.findById(userId)))
        .subscribe({
          next: (updatedPerson) => {
            this.currentPerson = updatedPerson;
            if (updatedPerson.picture) {
              this.imagePreview = Urls.getS3() + updatedPerson.picture;
            }
            this.updateCurrentUserInStorage(updatedPerson);
            this.handlePasswordChange();
          },
          error: (error) => {
            this.errorHandlerService.handle(error, "Erro ao atualizar perfil.");
            this.isSaving = false;
          },
        });
    }

    // Se há alteração de senha, fazer primeiro
    const newPassword = this.settingsForm.get("newPassword")?.value;
    const confirmPassword = this.settingsForm.get("confirmPassword")?.value;
    const currentPassword = this.settingsForm.get("currentPassword")?.value;

    if (newPassword && currentPassword && confirmPassword) {
      this.changePasswordService.changePassword(currentPassword, newPassword, confirmPassword).subscribe({
        next: () => {
          this.toast.success("Senha alterada com sucesso!", "Sucesso");
          this.isSaving = false;
          this.clearPasswordFields();
        },
        error: (error) => {
          this.errorHandlerService.handle(error, "Erro ao alterar senha.");
          this.isSaving = false;
        },
      });
    }
  }

  private clearPasswordFields(): void {
    this.settingsForm.patchValue({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  }

  private handlePasswordChange(): void {
    this.clearPasswordFields();
    this.selectedFile = null;
    this.isSaving = false;
    this.toast.success("Perfil atualizado com sucesso!", "Sucesso");
  }

  private updateCurrentUserInStorage(person: Person): void {
    // Atualizar currentUser no localStorage com novos dados
    const currentUserString = localStorage.getItem("currentUser") || sessionStorage.getItem("currentUser");
    if (currentUserString) {
      try {
        const currentUser = JSON.parse(currentUserString);
        // Atualizar nome, email e foto no currentUser
        const photoUrl = person.picture
          ? Urls.getS3() + person.picture
          : currentUser.photoUrl || currentUser.photo || currentUser.picture || "";
        const updatedUser = {
          ...currentUser,
          name: person.name,
          email: person.email,
          photoUrl: photoUrl,
        };
        this.authService.setCurrentUser(updatedUser);
      } catch (error) {
        // Se falhar, buscar do backend
        this.authService.getCurrentUser().subscribe({
          next: (user) => {
            const photoUrl = person.picture
              ? Urls.getS3() + person.picture
              : (user as any).photoUrl || (user as any).photo || "";
            const updatedUser = {
              ...user,
              name: person.name,
              email: person.email,
              photoUrl: photoUrl,
            } as any;
            this.authService.setCurrentUser(updatedUser);
          },
        });
      }
    }
  }
}
